import { ShieldAlert } from 'lucide-react';
import { Navigate, useParams } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { FormCardSkeleton } from '../../../shared/components/FormCardSkeleton';
import { PageHeader } from '../../../shared/components/PageHeader';
import { withinSection } from '../../../shared/components/section-trail';
import { tripPaths } from '../../trips/trips.paths';
import type { DispatchTripTab } from '../dispatch.paths';
import { DispatchTripBody } from '../components/DispatchTripBody';
import { useDispatchTrip } from '../hooks/use-dispatch-trip';

/**
 * The last look before a trip leaves: crew, route, readiness, eligibility and the allocations, then Dispatch (gitaumoses4/chukua#53).
 * The other tabs this page used to carry now live on the trip's own page, so their old URLs go there.
 */
export function DispatchTripPage({ tab }: { tab: DispatchTripTab }) {
  const { id = '' } = useParams();
  if (tab !== 'overview') return <Navigate to={tripPaths.tripTab(id, tab)} replace />;
  return <DispatchTripOverviewPage tripId={id} />;
}

function DispatchTripOverviewPage({ tripId }: { tripId: string }) {
  const { state, reload, dispatch, isSubmitting, error } = useDispatchTrip(tripId);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  const title = state.status === 'loaded' ? `Dispatch ${state.preview.trip.number}` : 'Dispatch trip';

  return (
    <>
      <PageHeader breadcrumbs={withinSection('dispatch')} title={title} description="Check the crew and what goes with them, then send the trip out." />
      {state.status === 'loading' && <FormCardSkeleton label="Loading dispatch" fields={4} />}
      {state.status === 'not-found' && <EmptyState icon={ShieldAlert} title="Trip not found" description="This trip does not exist." />}
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load this trip" description="Something went wrong. Try reloading the page." />
      )}
      {state.status === 'loaded' && (
        <DispatchTripBody preview={state.preview} isSubmitting={isSubmitting} error={error} onDispatch={dispatch} onOverridden={reload} />
      )}
    </>
  );
}
